import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Link, useSearchParams } from "react-router-dom";
import authApiService from "../services/authServices";
import { jwtDecode } from "jwt-decode";
import { ThreeDots } from "react-loader-spinner";
import Modal from "../utils/modalUtils";


function Schedule() {
   const [scheduleView, setScheduleView] = useState([]);
   const [userId, setUserId] = useState(null);
   const [userRole, setUserRole] = useState(null);
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      decodeToken();
   }, []);

   useEffect(() => {
      if (userId) {
         fetchScheduleView();
      }
   }, [userId]);

   const decodeToken = () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
         try {
            const decoded = jwtDecode(token);
            setUserId(decoded.userId);
            setUserRole(decoded.role || null);
         } catch (error) {
            console.error("Error decoding token:", error);
         }
      }
   };



   const [searchParams] = useSearchParams();
   const MRId = searchParams.get("id");
   const urlUserId = searchParams.get("userId"); 
   const urlSeasonId = searchParams.get("seasonId");

  useEffect(() => {
      fetchScheduleView();
   }, [userId, MRId, urlUserId]);

   const fetchScheduleView = async () => {
      // Priority: URL userId > token userId > MRId
      const idToSend = urlUserId || userId || MRId;
      if (!idToSend) {
         console.log("Waiting for userId or MRId...");
         return;
      }

      // Priority for season filter:
      // URL seasonId (member schedule button) > localStorage selected season (self navigation).
      const selectedSeasonId = urlSeasonId || localStorage.getItem("selectedSeasonId");

      try {
         console.log("Fetching schedule for id:", idToSend, "seasonId:", selectedSeasonId);
         setIsLoading(true);

         const params = { id: idToSend };
         if (selectedSeasonId && selectedSeasonId !== "0") {
            params.seasonId = selectedSeasonId;
         }

         const response = await authApiService.get("scheduleview", params);
         setScheduleView(response?.data || []);
      } catch (error) {
         console.error("Error fetching schedule data:", error);
         setScheduleView([]);
      } finally {
         setIsLoading(false); 
      }
   };

   // Re-fetch schedule when season changes (dropdown "CHOOSE ANOTHER SEASON")
   useEffect(() => {
      const handleSeasonChange = () => {
         fetchScheduleView();
      };

      window.addEventListener("seasonChange", handleSeasonChange);
      return () => {
         window.removeEventListener("seasonChange", handleSeasonChange);
      };
   }, [userId, MRId, urlUserId, urlSeasonId]);
   console.log("setScheduleView", scheduleView)


   const columns = [
      {
         name: "OPPONENT",
         selector: (row) => {
            if (row.full_name === "Unknown Player") {
               return "TBD";
            }
            return row.full_name || "-";
         },
         sortable: true,

      },
      {
         name: "LOCATION",
         selector: (row) => {
            if (row.full_name === "Unknown Player") {
               return "-";
            }
            return row.location || "-";
         },
         sortable: false,

      },
      {
         name: "PLAY BY",
         selector: (row) => row.play_by ? new Date(row.play_by).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
         }) : "-",
         sortable: true,

      },
      {
         name: "PLAYED",
         cell: (row) => {
            if (row.full_name === "Unknown Player") {
               return "-";
            }
            const opponentUserId = row.opponentId ?? row.golfer2;
            return row.played ?
               new Date(row.played).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
               })
               : <Link to={`/carter/schedule/printcard?matchId=${row.matchId}&userId=${opponentUserId}&full_name=${encodeURIComponent(row.full_name)}`}>Print Card</Link>;
         },
         ignoreRowClick: true,
         allowOverflow: true,
         button: true,


      },
      {
         name: "RESULTS",
         cell: (row) => {
            if (row.full_name === "Unknown Player") {
               return "-";
            }
            if (row.played) {
               const resultUserId = row.opponentId ?? "";
               const resultSeasonId = row.season_id ?? "";
               return (
                  <Link to={`/carter/schedule/viewresult?matchId=${row.matchId}&userId=${resultUserId}&scheduleUserId=${urlUserId || userId || ''}&seasonId=${resultSeasonId}`}>View Results</Link>
               );
            }

            // Only allow Post Results if:
            // 1. User is admin (role === 1), OR
            // 2. The logged-in user is one of the players in this match
            const loggedInId = String(userId || '');
            const isMatchPlayer =
               String(row.playerId) === loggedInId ||
               String(row.golfer2) === loggedInId ||
               String(row.opponentId) === loggedInId;
            const canPost = userRole === 1 || isMatchPlayer;

            const opponentUserId = row.opponentId ?? row.golfer2;
            return (
               <Link 
                  to={`/carter/schedule/postresult?matchId=${row.matchId}&userId=${opponentUserId}&full_name=${encodeURIComponent(row.full_name)}&scheduleUserId=${urlUserId || userId || ''}&seasonId=${row.season_id ?? ''}`}
                  onClick={(e) => {
                     const accessToken = localStorage.getItem("accessToken");
                     if (!accessToken) {
                        e.preventDefault();
                        Modal.fire({
                           icon: "error",
                           title: "Post Result",
                           text: "Please log in to post your score",
                           confirmButtonText: "OK"
                        });
                        return;
                     }

                     if (!canPost) {
                        e.preventDefault();
                        Modal.fire({
                           icon: "error",
                           title: "Post Result",
                           text: "You must be logged in and be one of the two players to post scores.",
                           confirmButtonText: "OK"
                        });
                     }
                  }}
               >
                  Post Scores
               </Link>
            );
         },
         ignoreRowClick: true,
         allowOverflow: true,
         button: true,
      }
   ];

   const customStyles = {
      table: {
         style: {
            border: "1px solid #ddd",
         },
      },
      headRow: {
         style: {
            backgroundColor: "#40444c",
            color: "#ffffff",
         },
      },
      rows: {
         style: {
            "&:nth-of-type(odd)": {
               backgroundColor: "#dedfdd",
            },
            "&:nth-of-type(even)": {
               backgroundColor: "#ffffff",
            },
         },
      },
   };

   const accessToken = localStorage.getItem("accessToken");

   return (
      <div className="page-container col-sm-9 schedule-page">
         {isLoading ? (
            <div
               className="loader-container"
               style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
               }}
            >
               <ThreeDots
                  height="80"
                  width="80"
                  radius="9"
                  color="#4fa94d"
                  ariaLabel="three-dots-loading"
                  visible={true}
               />
            </div>
         ) : scheduleView.length === 0 ? (
            <>
               <table class="schedule" id="match">
                  <thead>
                     <tr>
                        <th class="opp">Opponent</th>
                        <th>Location</th>
                        <th>Play By</th>
                        <th>Played</th>
                        <th>Results</th>
                     </tr>
                  </thead>
                  <tbody>
                  <tr><td colspan="5" class="blank-row"> &nbsp;</td></tr>
                     <tr class="odd">
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td><a href="#">Print Card</a></td>
                        <td><a class="ie-post-link" onclick="showAlertMsg();">Post Results</a></td>
                     </tr>
                     <tr class="even">
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td><a href="#">Print Card</a></td>
                        <td><a class="ie-post-link" onclick="showAlertMsg();">Post Results</a></td>
                     </tr>
                     <tr class="odd">
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td><a href="#">Print Card</a></td>
                        <td><a class="ie-post-link" onclick="showAlertMsg();">Post Results</a></td>
                     </tr>
                     <tr class="even">
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td><a href="#">Print Card</a></td>
                        <td><a class="ie-post-link" onclick="showAlertMsg();">Post Results</a></td>
                     </tr>
                  </tbody>
               </table>
               {!accessToken && !urlUserId && (
                  <div className="text-center" style={{ color: "red", marginTop: "20px", fontSize: "18px", fontWeight: "bold" }}>Member please <a href="/" style={{ color: "red", marginTop: "20px", fontSize: "18px", fontWeight: "bold" }}>Log in</a> to view your schedule.</div>
               )}
            </>
         ) : (
            <div className="leader-tab-info">
               <DataTable
                  columns={columns}
                  data={scheduleView}
                  pagination={scheduleView.length > 10}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  paginationPerPage={10}
               />
            </div>
         )}
      </div>
         
   );
}

export default Schedule;
